/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useState, useEffect, useRef} from 'react'
import {FormattedMessage} from 'react-intl'
import PropTypes from 'prop-types'
import {
    Box,
    Image,
    useBreakpointValue,
    Text
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Swiper, SwiperSlide} from 'swiper/react'
import 'swiper/css'
import 'swiper/css/free-mode'
import 'swiper/css/navigation'
import 'swiper/css/thumbs'
import {FreeMode, Navigation, Thumbs, Pagination, Zoom} from 'swiper/modules'
import CloudinaryImage from '../../cloudinary-media/image'
import Video from './video'
import {getUrl} from '../../../utils/cloudinary-utils'
import useTagManager from '../../../hooks/use-tag-manager'
import {scheduleIdleAnalytics} from '../../../utils/common-utils'

const isVideoAsset = (image) =>
    image?.disBaseLink?.includes('video') || image?.disBaseLink?.includes('mp4')

const MobileGallery = ({
    images,
    setMobileImageIndex,
    productName,
    desktopGalleryEngImages,
    isCart = false
}) => {
    // Safely inject the Engraved images in the Desktop Gallery.
    // 1. Remove already existing engraved images
    let cleanedImages = images.filter((img) => !img.isEngravedImg)

    // 2. Collect fresh engraved images
    const engravedImages = []
    if (desktopGalleryEngImages) {
        Object.values(desktopGalleryEngImages).forEach((face) => {
            if (face?.image) {
                const engravedTitle = `Engraved ${face.name} Side`

                if (!engravedImages.some((img) => img.title === engravedTitle)) {
                    engravedImages.push({
                        alt: engravedTitle,
                        title: engravedTitle,
                        isEngravedImg: true,
                        disBaseLink: face.image,
                        link: face.image
                    })
                }
            }
        })
    }

    // 3. Inject engraved images based on condition
    if (
        engravedImages?.length === 1 &&
        engravedImages[0].title &&
        engravedImages[0].title?.includes('Back')
    ) {
        // Insert after 1st image (if at least one image exists)
        if (cleanedImages?.length > 0) {
            cleanedImages = [cleanedImages[0], engravedImages[0], ...cleanedImages.slice(1)]
        } else {
            // no normal images → just return the engraved one
            cleanedImages = [engravedImages[0]]
        }
    } else {
        // Prepend engraved images
        cleanedImages = [...engravedImages, ...cleanedImages]
    }

    const mainSwiperRef = useRef(null)
    const tagManager = useTagManager()
    const videoWidth = useBreakpointValue({base: 490, md: 768})
    const [activeIndex, setActiveIndex] = useState(0)
    const [showPinchText, setShowPinchText] = useState(true)
    const [removePinchText, setRemovePinchText] = useState(false)
    const cleanedImagesKey = JSON.stringify(cleanedImages.map((image) => image.disBaseLink))

    useEffect(() => {
        const swiper = mainSwiperRef.current
        if (swiper && !swiper.destroyed) {
            swiper.slideTo(0, 0)
        }
        setActiveIndex(0)
        setMobileImageIndex?.(0)
    }, [cleanedImagesKey, setMobileImageIndex])

    useEffect(() => {
        setTimeout(() => {
            setShowPinchText(false)
        }, 2000)
        setTimeout(() => {
            setRemovePinchText(true)
        }, 2500)
    }, [])

    useEffect(() => {
        const handleTouchMove = (e) => {
            const zoomed = document.querySelector('.swiper-slide-zoomed')
            if (zoomed && e.target.closest('.swiper-zoom-container')) {
                e.preventDefault()
            }
        }

        document.addEventListener('touchmove', handleTouchMove, {passive: false})

        return () => {
            document.removeEventListener('touchmove', handleTouchMove)
        }
    }, [])
    return (
        <Box maxWidth="100vw" className="mobile-gallery-wrapper">
            <Box
                className="mobile-gallery-box"
                width={isCart ? 'unset' : '100vw'}
                maxWidth="100vw"
                position="relative"
                sx={{
                    '.swiper:not(.swiper-initialized) .swiper-wrapper': {
                        display: 'flex',
                        overflowX: 'hidden'
                    },
                    '.swiper:not(.swiper-initialized) .swiper-slide': {
                        width: '100vw',
                        flex: '0 0 100vw'
                    },
                    '.swiper-pagination': {
                        position: 'absolute',
                        left: '15px',
                        bottom: '15px',
                        textAlign: 'left',
                        width: 'auto',
                        background: 'gradientImageView',
                        backdropFilter: 'blur(3.469388008117676px)',
                        padding: '4px 12px',
                        lineHeight: '22px',
                        borderRadius: '51.347px',
                        color: 'white',
                        fontSize: '13px'
                    },
                    '& .swiper-slide': {
                        background: 'gray.100'
                    },
                    '.swiper-pagination .pagination-video-icon': {
                        width: '11px',
                        height: '11px',
                        marginRight: '3px',
                        marginTop: '1px',
                        display: 'none'
                    },
                    '.swiper-pagination .pagination-content': {
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px'
                    }
                }}
            >
                {!isCart && (
                    <Box
                        alignItems="center"
                        className="pinch-to-zoom"
                        gap="8px"
                        padding="5px 10px"
                        backgroundColor="blackAlpha610"
                        borderRadius="3px"
                        width="fit-content"
                        display="inline-flex"
                        top="50%"
                        left="50%"
                        position="absolute"
                        transform="translate(-50%, -50%)"
                        transition="all, 0.3s ease"
                        zIndex={removePinchText ? '0' : '2'}
                        opacity={showPinchText ? 1 : 0}
                    >
                        <Image
                            alt="Pinch to Zoom"
                            height="12px"
                            width="12px"
                            src={getUrl('UIImages/images/pdp-image-zoom-white.svg')}
                        />
                        <Text
                            color="white"
                            className="zoom-text"
                            letterSpacing="0.08px"
                            lineHeight="26px"
                            fontSize="16px"
                            fontWeight="300"
                        >
                            <FormattedMessage id="pinch.to.zoom" defaultMessage="Pinch to Zoom" />
                        </Text>
                    </Box>
                )}
                <Swiper
                    onSwiper={(swiper) => {
                        mainSwiperRef.current = swiper
                    }}
                    zoom={true}
                    onSlideChange={(swiper) => {
                        setActiveIndex(swiper.activeIndex)
                        const isVideo = isVideoAsset(cleanedImages[swiper.activeIndex])

                        const icon = document.querySelector('.pagination-video-icon')

                        if (icon) {
                            if (isVideo) {
                                icon.src = getUrl('UIImages/images/play-video-icon.svg')
                                icon.style.display = 'inline-block'
                            } else {
                                icon.style.display = 'none'
                            }
                        }
                    }}
                    onTransitionEnd={(e) => {
                        if (setMobileImageIndex && e.progress) {
                            const scrollDepth = (e.progress * 100).toFixed(0) + '%'
                            scheduleIdleAnalytics(() =>
                                tagManager.productScroll(scrollDepth, productName)
                            )
                            setMobileImageIndex(e.activeIndex)
                        }
                    }}
                    onDoubleClick={(e) => {
                        const isImage = e.clickedSlide?.querySelector(
                            '.swiper-zoom-container[data-type="image"]'
                        )
                        const zoomed = document.querySelector('.swiper-slide-zoomed')
                        if (zoomed && isImage) {
                            scheduleIdleAnalytics(() =>
                                tagManager.pdpCtaClick('cta click - image zoom', productName)
                            )
                        }
                    }}
                    pagination={{
                        type: 'fraction',
                        renderFraction: function (currentClass, totalClass) {
                            return `
                                <span class="pagination-content">
                                    <img class="pagination-video-icon" />
                                    <span class="${currentClass}"></span>
                                    /
                                    <span class="${totalClass}"></span>
                                </span>
                            `
                        }
                    }}
                    spaceBetween={0}
                    modules={[FreeMode, Navigation, Thumbs, Pagination, Zoom]}
                    className="pdp-slider-main"
                >
                    {cleanedImages?.map((image, index) => {
                        const isVideo = isVideoAsset(image)

                        return (
                            <SwiperSlide key={index}>
                                <div
                                    className="swiper-zoom-container"
                                    data-type={isVideo ? 'video' : 'image'}
                                >
                                    {isVideo ? (
                                        <Video
                                            src={image.disBaseLink}
                                            videoAlt={image.alt}
                                            videoEnableSound={true}
                                            videoDisableAutoPlay={index !== activeIndex}
                                            aspectRatio="469:587"
                                            width={videoWidth}
                                        />
                                    ) : image?.isEngravedImg ? (
                                        <Image
                                            src={image.disBaseLink}
                                            alt={image.alt}
                                            mixBlendMode="multiply"
                                            loading={index === 0 ? 'eager' : 'lazy'}
                                            fetchPriority={index === 0 ? 'high' : 'auto'}
                                        />
                                    ) : (
                                        <CloudinaryImage
                                            src={image.disBaseLink}
                                            alt={image.alt}
                                            lazyload={index !== 0}
                                            width={450}
                                            aspectRatio="469:587"
                                            imgSizes={{base: '100vw'}}
                                            sizes={[450, 650, 768, 1152, 1290, 1536, 1920]}
                                            styles={{
                                                aspectRatio: '469/587',
                                                width: '100%',
                                                mixBlendMode: 'multiply',
                                                height: 'auto',
                                                htmlWidth: 469,
                                                htmlHeight: 587
                                            }}
                                        />
                                    )}
                                </div>
                            </SwiperSlide>
                        )
                    })}
                </Swiper>
            </Box>
        </Box>
    )
}

MobileGallery.propTypes = {
    images: PropTypes.arrayOf(
        PropTypes.shape({
            disBaseLink: PropTypes.string,
            alt: PropTypes.string
        })
    ),
    setMobileImageIndex: PropTypes.func,
    desktopGalleryEngImages: PropTypes.any,
    productName: PropTypes.any,
    isCart: PropTypes.bool
}

export default MobileGallery
